import { useState, useEffect, useRef } from "react";
import { Plus, Pencil, Trash2, Upload, ImageIcon, CalendarIcon, X, Link2 } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import axios from "axios";
import { API, useAuth } from "@/App";
import { toast } from "sonner";

// `image` stays as the single featured/cover image (unchanged, backward compatible).
// `images` is new: an array of gallery image URLs attached to the post.
// `slug` is now editable directly, so a post's URL can be corrected without deleting and recreating it.
const emptyPost = { title: "", slug: "", excerpt: "", content: "", image: "", images: [], category: "nutrition", tags: [], author: "Praroha Greens", published: true, created_at: "" };

function slugify(text) {
  return (text || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
}

function toLocalDatetime(isoStr) {
  if (!isoStr) return "";
  try {
    const d = new Date(isoStr);
    if (isNaN(d.getTime())) return "";
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch { return ""; }
}

function fromLocalDatetime(localStr) {
  if (!localStr) return "";
  return new Date(localStr).toISOString();
}

function resolveImageUrl(url) {
  if (!url) return url;
  return url.startsWith("/api") ? `${API.replace("/api", "")}${url}` : url;
}

export default function AdminBlog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyPost);
  // Tracks whether the person has hand-edited the Slug field directly, so we stop
  // auto-following the Title once they've customized it.
  const [slugEdited, setSlugEdited] = useState(false);
  const { getToken } = useAuth();
  const headers = { Authorization: `Bearer ${getToken()}` };

  const fetchPosts = async () => {
    try {
      const res = await axios.get(`${API}/admin/blog`, { headers });
      setPosts(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchPosts(); }, []);

  const openCreate = () => { setEditing(null); setForm(emptyPost); setSlugEdited(false); setDialogOpen(true); };
  const openEdit = (p) => { setEditing(p.id); setForm({ ...emptyPost, ...p, images: p.images || [] }); setSlugEdited(true); setDialogOpen(true); };

  const handleSave = async () => {
    try {
      const finalSlug = slugify(form.slug || form.title);
      if (!finalSlug) {
        toast.error("Title or Slug is required");
        return;
      }
      if (editing) {
        await axios.put(`${API}/admin/blog/${editing}`, { ...form, slug: finalSlug }, { headers });
        toast.success("Post updated");
      } else {
        await axios.post(`${API}/admin/blog`, { ...form, slug: finalSlug }, { headers });
        toast.success("Post created");
      }
      setDialogOpen(false);
      fetchPosts();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this blog post?")) return;
    try {
      await axios.delete(`${API}/admin/blog/${id}`, { headers });
      toast.success("Deleted");
      fetchPosts();
    } catch (e) { toast.error("Delete failed"); }
  };

  const updateForm = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const updateTitle = (value) => {
    setForm(prev => ({
      ...prev,
      title: value,
      // Auto-fill the slug from the title until the person types into Slug directly.
      slug: slugEdited ? prev.slug : slugify(value),
    }));
  };

  const updateSlug = (value) => {
    setSlugEdited(true);
    updateForm("slug", value);
  };

  // --- Multi-image upload for a single post ---
  // form.images holds every image; images[0] doubles as the post's cover/featured
  // image, so `image` (used elsewhere on the site) always stays in sync.
  const [uploadingImages, setUploadingImages] = useState(false);
  const imagesFileRef = useRef(null);

  const handleImagesUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploadingImages(true);
    try {
      // Uploaded sequentially since the endpoint accepts one file per request.
      const uploadedUrls = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append("image", file);
        const res = await axios.post(`${API}/admin/upload-image`, fd, { headers: { ...headers, "Content-Type": "multipart/form-data" } });
        uploadedUrls.push(res.data.url);
      }
      setForm(prev => {
        const images = [...(prev.images || []), ...uploadedUrls];
        return { ...prev, images, image: prev.image || images[0] };
      });
      toast.success(`${uploadedUrls.length} image${uploadedUrls.length > 1 ? "s" : ""} uploaded`);
    } catch {
      toast.error("One or more uploads failed");
    } finally {
      setUploadingImages(false);
      if (imagesFileRef.current) imagesFileRef.current.value = "";
    }
  };

  const removeImage = (idx) => {
    setForm(prev => {
      const images = prev.images.filter((_, i) => i !== idx);
      return { ...prev, images, image: images[0] || "" };
    });
  };

  const moveImage = (idx, dir) => {
    setForm(prev => {
      const arr = [...prev.images];
      const newIdx = idx + dir;
      if (newIdx < 0 || newIdx >= arr.length) return prev;
      [arr[idx], arr[newIdx]] = [arr[newIdx], arr[idx]];
      return { ...prev, images: arr, image: arr[0] };
    });
  };

  return (
    <div className="space-y-4" data-testid="admin-blog">
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-2xl font-bold text-gray-900">Blog Posts ({posts.length})</h1>
        <Button onClick={openCreate} className="bg-praroha-forest hover:bg-praroha-forest/90" data-testid="add-blog-btn">
          <Plus className="w-4 h-4 mr-2" /> New Post
        </Button>
      </div>

      {loading ? (
        <div className="space-y-3">{[...Array(3)].map((_, i) => <div key={i} className="h-16 bg-gray-200 animate-pulse rounded-lg" />)}</div>
      ) : (
        <Card className="border-gray-200">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr className="text-left">
                    <th className="px-4 py-3 font-medium text-gray-500">Title</th>
                    <th className="px-4 py-3 font-medium text-gray-500">Category</th>
                    <th className="px-4 py-3 font-medium text-gray-500">Author</th>
                    <th className="px-4 py-3 font-medium text-gray-500">Status</th>
                    <th className="px-4 py-3 font-medium text-gray-500">Date</th>
                    <th className="px-4 py-3 font-medium text-gray-500">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {posts.map((p) => (
                    <tr key={p.id} className="hover:bg-gray-50">
                      <td className="px-4 py-2 font-medium text-gray-800 max-w-xs truncate">{p.title}</td>
                      <td className="px-4 py-2"><span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full text-xs capitalize">{p.category}</span></td>
                      <td className="px-4 py-2 text-gray-600">{p.author}</td>
                      <td className="px-4 py-2">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${p.published ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                          {p.published ? "Published" : "Draft"}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-gray-500 text-xs">{new Date(p.created_at).toLocaleDateString()}</td>
                      <td className="px-4 py-2">
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" onClick={() => openEdit(p)}><Pencil className="w-3.5 h-3.5" /></Button>
                          <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700" onClick={() => handleDelete(p.id)}><Trash2 className="w-3.5 h-3.5" /></Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="blog-dialog">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit Post" : "New Post"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Title</Label><Input value={form.title} onChange={e => updateTitle(e.target.value)} data-testid="blog-title-input" /></div>

            <div>
              <Label className="flex items-center gap-1.5"><Link2 className="w-3.5 h-3.5" /> Slug (URL)</Label>
              <Input
                value={form.slug}
                onChange={e => updateSlug(e.target.value)}
                placeholder={slugify(form.title) || "auto-generated-from-title"}
                data-testid="blog-slug-input"
              />
              <p className="text-xs text-muted-foreground mt-1">
                prarohagreens.com/blog/<span className="font-mono">{form.slug || slugify(form.title) || "your-slug-here"}</span>
                {" · "}Auto-fills from Title until you edit it directly. Changing this on a published post breaks any links already shared to the old URL.
              </p>
            </div>

            <div><Label>Excerpt</Label><Input value={form.excerpt} onChange={e => updateForm("excerpt", e.target.value)} /></div>
            <div><Label>Content</Label><Textarea value={form.content} onChange={e => updateForm("content", e.target.value)} rows={6} /></div>

            <div>
              <Label className="flex items-center gap-1.5"><ImageIcon className="w-3.5 h-3.5" /> Images ({(form.images || []).length})</Label>
              <p className="text-xs text-muted-foreground mt-0.5 mb-1.5">First image is used as the post's cover/thumbnail. Drag order with the arrows.</p>
              <div className="space-y-2">
                {form.images?.length > 0 && (
                  <div className="grid grid-cols-3 gap-2">
                    {form.images.map((url, idx) => (
                      <div key={idx} className="relative group w-full aspect-square rounded-lg overflow-hidden border bg-gray-50">
                        <img src={resolveImageUrl(url)} alt={`Image ${idx + 1}`} className="w-full h-full object-cover" />
                        {idx === 0 && (
                          <span className="absolute top-1 left-1 bg-praroha-forest text-white text-[9px] font-medium px-1.5 py-0.5 rounded">Cover</span>
                        )}
                        <button
                          type="button"
                          onClick={() => removeImage(idx)}
                          className="absolute top-1 right-1 bg-black/60 hover:bg-black/80 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                          data-testid={`remove-image-${idx}`}
                        >
                          <X className="w-3 h-3" />
                        </button>
                        <div className="absolute bottom-1 left-1 right-1 flex justify-between opacity-0 group-hover:opacity-100 transition-opacity">
                          <button type="button" disabled={idx === 0} onClick={() => moveImage(idx, -1)} className="bg-black/60 hover:bg-black/80 text-white text-[10px] rounded px-1.5 py-0.5 disabled:opacity-30">◀</button>
                          <button type="button" disabled={idx === form.images.length - 1} onClick={() => moveImage(idx, 1)} className="bg-black/60 hover:bg-black/80 text-white text-[10px] rounded px-1.5 py-0.5 disabled:opacity-30">▶</button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <input ref={imagesFileRef} type="file" accept="image/*" multiple className="hidden" onChange={handleImagesUpload} />
                <Button type="button" variant="outline" size="sm" disabled={uploadingImages} onClick={() => imagesFileRef.current?.click()} className="w-full" data-testid="blog-images-upload-btn">
                  <Upload className="w-4 h-4 mr-1.5" /> {uploadingImages ? "Uploading..." : "Upload Images"}
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Category</Label>
                <select className="w-full border rounded-md px-3 py-2 text-sm" value={form.category} onChange={e => updateForm("category", e.target.value)}>
                  <option value="nutrition">Nutrition</option>
                  <option value="recipes">Recipes</option>
                  <option value="growing">Growing</option>
                  <option value="lifestyle">Lifestyle</option>
                </select>
              </div>
              <div><Label>Author</Label><Input value={form.author} onChange={e => updateForm("author", e.target.value)} /></div>
            </div>
            <div>
              <Label className="flex items-center gap-1.5"><CalendarIcon className="w-3.5 h-3.5" /> Publish Date & Time</Label>
              <Input
                type="datetime-local"
                value={toLocalDatetime(form.created_at)}
                onChange={e => updateForm("created_at", fromLocalDatetime(e.target.value))}
                className="mt-1"
                data-testid="blog-datetime-input"
              />
              <p className="text-xs text-muted-foreground mt-1">Leave empty to use current date & time</p>
            </div>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.published} onChange={e => updateForm("published", e.target.checked)} /> Published</label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} className="bg-praroha-forest hover:bg-praroha-forest/90" data-testid="save-blog-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
